import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import type { CockpitPeriod } from '../../types';

const OPTIONS: Array<{ value: CockpitPeriod; label: string; sub?: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'quarter', label: 'This quarter' },
  { value: 'year', label: 'School year', sub: 'Jul 1 – Jun 30' },
];

/**
 * Compact period picker — a "This month ▾" button that opens a small popover of
 * the five ranges. Replaces the 5-wide inline toggle that overflowed on phones.
 * Same popover pattern as ListSortMenu (relative wrapper + outside-click close).
 */
export function PeriodSelect({ value, onChange }: { value: CockpitPeriod; onChange: (p: CockpitPeriod) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const current = OPTIONS.find((o) => o.value === value) ?? OPTIONS[2];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        {current.label}
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-30 mt-1 card w-44 overflow-hidden shadow-xl">
          {OPTIONS.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => { setOpen(false); onChange(o.value); }}
                className={`flex w-full items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-700/60 px-3 py-2.5 text-left text-sm transition-colors last:border-b-0 ${
                  active
                    ? 'bg-blast-50 text-blast-700 dark:bg-blast-950 dark:text-blast-300 font-semibold'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                }`}
              >
                <span>
                  {o.label}
                  {o.sub && <span className="block text-[11px] font-normal text-gray-400 dark:text-gray-500">{o.sub}</span>}
                </span>
                {active && <Check className="h-4 w-4 flex-none text-blast-600 dark:text-blast-400" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
